/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-this-alias */
/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable @typescript-eslint/ban-types */
import 'froala-editor/js/plugins.pkgd.min';

// @ts-ignore
import FroalaEditor from 'froala-editor';
import { Component, Input, OnChanges, OnDestroy } from '@angular/core';
import { AbstractControl, FormControl } from '@angular/forms';
import { CommunicationDefinitionResponse } from 'projects/shared/services/communications/models/communication-definition.response';
import { environment } from 'projects/shared/environments/environment';

interface OptionConfig {
  [key: string]: string
}

interface FroalaControlsObject {
  destroy: () => void;
  initialize: () => void;
  getEditor: Function;
}

let nextEditorId = 0;

@Component({
  selector: 'app-communication-form',
  templateUrl: './communication-form.component.html',
  standalone: false,
  styleUrls: ['./communication-form.component.scss'],
})
export class CommunicationFormComponent implements OnChanges, OnDestroy {
  @Input() subjectFormControl: FormControl;
  @Input() bodyFormControl: FormControl;
  @Input() communicationDefinition: CommunicationDefinitionResponse;
  @Input() culture: string;
  @Input() width?: number;
  @Input() height?: number;
  froalaOptions: Object | null;
  initControls: FroalaControlsObject;
  showEditor = true;
  readonly editorId = `froala-${nextEditorId++}`;
  private remountTimer: ReturnType<typeof setTimeout> | null = null;

  ngOnChanges(): void {
    if (!this.froalaOptions) {
      // First render — build options so the template can mount the directive
      this.buildFroalaEditor();
    } else {
      // Subsequent changes — cancel any pending remount, then debounce a fresh one
      if (this.remountTimer !== null) {
        clearTimeout(this.remountTimer);
        this.remountTimer = null;
      }
      this.destroyEditor();
      this.showEditor = false;
      this.buildFroalaEditor();
      this.remountTimer = setTimeout(() => {
        this.showEditor = true;
        this.remountTimer = null;
      });
    }
  }

  ngOnDestroy(): void {
    if (this.remountTimer !== null) {
      clearTimeout(this.remountTimer);
      this.remountTimer = null;
    }
    this.destroyEditor();
  }

  froalaInit(initControls: Object) {
    // a stale editor from a previous mount must never survive a new initialization
    this.destroyEditor();
    this.initControls = <FroalaControlsObject>initControls;
    this.initControls.initialize();
  }

  private destroyEditor(): void {
    try {
      this.initControls?.destroy();
    } catch {
      // editor was already destroyed
    }
    this.initControls = undefined as unknown as FroalaControlsObject;
  }

  addParameter(control: AbstractControl, parameter: string): void {
    const subject = `${control.value} %%${parameter}%%`;
    control.setValue(subject);
    control.markAsDirty();
    control.updateValueAndValidity();
  }

  private mapAvailableParametersToOptionConfig(): OptionConfig {
    const optionConfig: OptionConfig = {};
    this.communicationDefinition.availableParameters.forEach(
      (property, index) => (optionConfig[property.code] = this.communicationDefinition.availableParameters[index].code)
    );
    return optionConfig;
  }

  private buildFroalaEditor(): void {
    const self = this;
    FroalaEditor.DefineIcon('parameters', { NAME: '%', template: 'text' });
    FroalaEditor.RegisterCommand('parameters', {
      title: 'Available parameters',
      type: 'dropdown',
      icon: 'parameters',
      focus: false,
      undo: true,
      refreshAfterCallback: false,
      options: self.mapAvailableParametersToOptionConfig(),
      callback: function (cmd: any, val: string) {
        this.html.insert(` %%${val}%%`);
        this.undo.saveStep();
      },
    });

    const toolbarButtons = this.bodyFormControl.disabled
      ? []
      : [
          'bold',
          'italic',
          'underline',
          'strikeThrough',
          'subscript',
          'superscript',
          '|',
          'fontFamily',
          'fontSize',
          'color',
          'inlineStyle',
          'inlineClass',
          'clearFormatting',
          '|',
          'paragraphFormat',
          'lineHeight',
          'paragraphStyle',
          'align',
          'formatOL',
          'formatUL',
          'outdent',
          'indent',
          'quote',
          'specialCharacters',
          '-',
          'insertLink',
          'insertImage',
          'insertTable',
          'parameters',
          '|',
          'insertHR',
          'selectAll',
          'print',
          'html',
          'fullscreen',
          '|',
          'undo',
          'redo',
        ];

    this.froalaOptions = {
      key: environment.froalaEditorKey,
      fullPage: true,
      useClasses: false,
      iframeStyleFiles: ['/assets/styles/froala-custom.css'],
      charCounterCount: true,
      heightMin: 300,
      heightMax: 1000,
      height: self.height ?? 'auto',
      width: self.width ?? 'auto',
      imageUploadRemoteUrls: false,
      htmlAllowedTags: ['.*'],
      htmlUntouched: true,
      tableStyles: {
        'fr-dashed-borders': 'Dashed Borders',
        'fr-alternate-rows': 'Alternate Rows',
        noborder: 'No borders',
      },
      tableCellStyles: {
        'fr-highlighted': 'Highlighted',
        'fr-thick': 'Thick',
        noborder: 'No borders',
      },
      toolbarButtons: toolbarButtons,
      quickInsertButtons: ['table', 'ol', 'ul', 'hr'],
      imageInsertButtons: ['imageByURL'],
      events: {
        // Need to manually set and get body content from Froala as the formControl binding is broken after reinitializing Froala
        contentChanged: () => {
          // Manually set body formcontrol after content has changed in froala editor
          const value = this.initControls?.getEditor().html.get();
          if (value === undefined || value === this.bodyFormControl.value) return;
          this.bodyFormControl.setValue(value);
          this.bodyFormControl.markAsDirty();
        },
        initialized: function (this: any) {
          // Manually set body content into Froala
          this.html.set(self.bodyFormControl.value);
        },
      },
    };
  }
}
